/**
 * useInvestigationResultsStore — the live investigation results of the current
 * run, persisted to sessionStorage so they survive leaving /workbench and a
 * reload (they used to live only in the hook's useState and died on unmount).
 *
 * One entry: the latest run. A new run replaces it; `resetWorkbenchToFresh`,
 * "Leave without saving" and the quota fallback clear it. The entry records the
 * root node it belongs to and the canvas version (hash of the address-node ids)
 * at the time it was written.
 *
 * Size: the blob shares the ~5 MB sessionStorage budget with the graph, so it is
 * slimmed with the same transaction slimming the transaction store uses and
 * capped at RESULTS_MAX_CHARS; the largest sections are dropped first when it
 * still does not fit.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createQuotaSafeSessionStorage } from '../utils/quotaSafeStorage';
import { capTransactionsForPersist, PERSIST_MAX_TXS_PER_NODE } from './useTransactionStore';
import { WORKBENCH_RESULTS_KEY } from '../data/workbenchDraft';

/** Per-entry cap in characters (the whole origin budget is shared; MAX_PERSIST_CHARS is 2M per key). */
export const RESULTS_MAX_CHARS = 900_000;
/** Generic arrays inside a section are cut to this many entries. */
export const RESULTS_MAX_ARRAY = 300;
const MAX_DEPTH = 14;

export interface PersistedSection {
    data: unknown;
    loadedAt?: string;
    health?: unknown;
    degradedReason?: string;
}

export interface PersistedResultsEntry {
    /** Chain of the root node the results belong to. */
    chain: string;
    /** Root address exactly as the run used it. */
    address: string;
    /** Hash of the canvas address-node ids when the entry was written. */
    canvasVersion: string;
    savedAt: number;
    /** The run had reached `complete` when written; false for a run cut short by leaving the page. */
    complete: boolean;
    sections: Record<string, PersistedSection>;
    circulation: unknown | null;
    assetRecovery: unknown | null;
    coverage: unknown | null;
    spinePromotion: unknown | null;
    /** Section keys dropped, or arrays trimmed, to fit the cap. */
    trimmed: string[];
}

const looksLikeTx = (o: unknown): boolean =>
    !!o && typeof o === 'object' && !Array.isArray(o) && 'tx_hash' in (o as object);

/** Slim a value for persistence: transaction arrays use the transaction store's cap and field list. */
export function slimResultValue(value: unknown, trimmed: string[], path: string, depth = 0): unknown {
    if (depth > MAX_DEPTH || value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) {
        if (value.length > 0 && value.every(looksLikeTx)) {
            const slim = capTransactionsForPersist(new Map([['k', value as any]])).get('k') ?? [];
            if (slim.length < value.length) trimmed.push(`${path}[${value.length}->${PERSIST_MAX_TXS_PER_NODE}]`);
            return slim;
        }
        let arr = value;
        if (arr.length > RESULTS_MAX_ARRAY) {
            trimmed.push(`${path}[${arr.length}->${RESULTS_MAX_ARRAY}]`);
            arr = arr.slice(0, RESULTS_MAX_ARRAY);
        }
        return arr.map((v, i) => slimResultValue(v, trimmed, `${path}[${i}]`, depth + 1));
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = slimResultValue(v, trimmed, `${path}.${k}`, depth + 1);
    }
    return out;
}

export interface BuildEntryInput {
    chain: string;
    address: string;
    canvasVersion: string;
    complete: boolean;
    sections: Record<string, PersistedSection>;
    circulation?: unknown;
    assetRecovery?: unknown;
    coverage?: unknown;
    spinePromotion?: unknown;
    maxChars?: number;
    now?: number;
}

/** Build a slimmed, size-capped entry. Returns null when nothing loaded or nothing fits. */
export function buildResultsEntry(input: BuildEntryInput): PersistedResultsEntry | null {
    const maxChars = input.maxChars ?? RESULTS_MAX_CHARS;
    const trimmed: string[] = [];
    const sections: Record<string, PersistedSection> = {};
    for (const [key, s] of Object.entries(input.sections)) {
        sections[key] = { ...s, data: slimResultValue(s.data, trimmed, key) };
    }
    if (Object.keys(sections).length === 0) return null;
    const entry: PersistedResultsEntry = {
        chain: input.chain,
        address: input.address,
        canvasVersion: input.canvasVersion,
        savedAt: input.now ?? Date.now(),
        complete: input.complete,
        sections,
        circulation: slimResultValue(input.circulation ?? null, trimmed, 'circulation'),
        assetRecovery: slimResultValue(input.assetRecovery ?? null, trimmed, 'assetRecovery'),
        coverage: input.coverage ?? null,
        spinePromotion: input.spinePromotion ?? null,
        trimmed,
    };
    const size = () => JSON.stringify(entry).length;
    // Drop the largest optional blocks first, then the largest sections.
    while (size() > maxChars) {
        const candidates: Array<[string, number]> = [
            ['assetRecovery', JSON.stringify(entry.assetRecovery).length],
            ['circulation', JSON.stringify(entry.circulation).length],
            ...Object.entries(entry.sections).map(([k, s]) => [`section:${k}`, JSON.stringify(s).length] as [string, number]),
        ].filter(([, n]) => (n as number) > 4) as Array<[string, number]>;
        if (candidates.length === 0) return null;
        candidates.sort((a, b) => b[1] - a[1]);
        const [name] = candidates[0];
        if (name.startsWith('section:')) delete entry.sections[name.slice(8)];
        else (entry as any)[name] = null;
        entry.trimmed.push(`dropped:${name}`);
        if (Object.keys(entry.sections).length === 0) return null;
    }
    return entry;
}

interface ResultsStoreState {
    entry: PersistedResultsEntry | null;
    save: (entry: PersistedResultsEntry) => void;
    clear: () => void;
}

export const useInvestigationResultsStore = create<ResultsStoreState>()(
    persist(
        (set) => ({
            entry: null,
            save: (entry) => set({ entry }),
            clear: () => set((st) => (st.entry === null ? st : { entry: null })),
        }),
        {
            name: WORKBENCH_RESULTS_KEY,
            storage: createJSONStorage(() => createQuotaSafeSessionStorage({
                warnKey: '__workbench_results_quota_warned__',
                warnMessage:
                    '[useInvestigationResultsStore] sessionStorage quota exceeded: investigation results will not persist across reloads.',
                maxChars: RESULTS_MAX_CHARS + 1000,
            })),
            partialize: (s) => ({ entry: s.entry }),
            version: 1,
        },
    ),
);
